import React, { useState, useEffect, useMemo } from 'react';
import {
  type CategoryStructure,
  type WedlogBizVendor,
  type VendorCreateRequest,
  type CategoryItem,
  type VendorFormSubmitPayload,
  type HostVendorCandidate
} from '../types';
import { formatBusinessNumber } from '../../../../../../utils/format';

const onlyDigits = (value: string) => value.replace(/\D/g, '').slice(0, 11);
const formatMobile010 = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};
const maskMobile = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length === 11) return `${digits.slice(0, 3)}-****-${digits.slice(7, 11)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-***-${digits.slice(6, 10)}`;
  return value || '-';
};
const isValidKoreanMobile010 = (value: string) => /^010\d{8}$/.test(onlyDigits(value));

interface VendorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedVendor: WedlogBizVendor | null;
  categories: CategoryStructure;
  categoryList: CategoryItem[];
  onSubmit: (payload: VendorFormSubmitPayload) => Promise<boolean>;
  onClearSelection: () => void;
}

export const VendorFormModal: React.FC<VendorFormModalProps> = ({ 
  isOpen, onClose, preSelectedVendor, categories, categoryList, onSubmit, onClearSelection
}) => {
  const initialFormState = {
    name: '',
    category: '',
    subCategory: '',
    contact: '',
    notiPhone: '',
    faxNumber: '',
    manager: '',
    email: '',
    address: '',
    businessNumber: '',
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    memo: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [hostSearchTerm, setHostSearchTerm] = useState('');
  const [hostCandidates, setHostCandidates] = useState<HostVendorCandidate[]>([]);
  const [isHostCandidatesLoading, setIsHostCandidatesLoading] = useState(false);
  const [hostCandidatesError, setHostCandidatesError] = useState<string | null>(null);
  const [selectedExistingHostIdx, setSelectedExistingHostIdx] = useState<number | null>(null);
  const [isFinderOpen, setIsFinderOpen] = useState(false);
  const [hostFilterCategoryIdx, setHostFilterCategoryIdx] = useState<number | null>(null);
  const topCategories = categoryList;
  const selectedParent = topCategories.find((item) => item.name === formData.category);
  const subCategoryOptions = selectedParent?.children || [];
  const hasCategoryOptions = topCategories.length > 0;
  const isExistingVendorSelected = selectedExistingHostIdx !== null;
  const selectedHostFilterLabel = useMemo(() => {
    if (!hostFilterCategoryIdx) return '전체';
    const parentMatch = topCategories.find((item) => item.vendorCategoryIdx === hostFilterCategoryIdx);
    if (parentMatch) return parentMatch.name;
    for (const parentItem of topCategories) {
      const childMatch = parentItem.children?.find((child) => child.vendorCategoryIdx === hostFilterCategoryIdx);
      if (childMatch) return `${parentItem.name} > ${childMatch.name}`;
    }
    return '전체';
  }, [hostFilterCategoryIdx, topCategories]);
  const hasExistingSearchCondition = hostSearchTerm.trim().length > 0 || hostFilterCategoryIdx !== null;
  const selectedExistingHost = useMemo(
    () => hostCandidates.find((candidate) => candidate.vendorIdx === selectedExistingHostIdx) || null,
    [hostCandidates, selectedExistingHostIdx]
  );

  const resolveCategorySelection = (candidate: Partial<HostVendorCandidate>) => {
    const fallbackParent = topCategories[0];
    let parentName = candidate.parentCategoryName || '';
    let childName = candidate.subCategoryName || '';

    if (!parentName && candidate.categoryName) {
      if (candidate.categoryName.includes('>')) {
        const [rawParent, rawChild] = candidate.categoryName.split('>').map((item) => item.trim());
        parentName = rawParent || '';
        childName = rawChild || childName;
      } else {
        parentName = candidate.categoryName.trim();
      }
    }

    if (parentName && childName) {
      const matchedParent = topCategories.find((item) => item.name === parentName);
      if (matchedParent) {
        const matchedChild = matchedParent.children?.find((child) => child.name === childName);
        return {
          category: matchedParent.name,
          subCategory: matchedChild?.name || matchedParent.children?.[0]?.name || ''
        };
      }
    }

    if (parentName) {
      const matchedParent = topCategories.find((item) => item.name === parentName);
      if (matchedParent) {
        return {
          category: matchedParent.name,
          subCategory: matchedParent.children?.[0]?.name || ''
        };
      }
    }

    if (childName) {
      const matchedParent = topCategories.find((item) =>
        item.children?.some((child) => child.name === childName)
      );
      if (matchedParent) {
        const matchedChild = matchedParent.children?.find((child) => child.name === childName);
        return {
          category: matchedParent.name,
          subCategory: matchedChild?.name || matchedParent.children?.[0]?.name || ''
        };
      }
    }

    return {
      category: fallbackParent?.name || '',
      subCategory: fallbackParent?.children?.[0]?.name || ''
    };
  };

  const applyExistingVendorToForm = (candidate: Partial<HostVendorCandidate>) => {
    const resolvedCategory = resolveCategorySelection(candidate);
    setFormData((prev) => ({
      ...prev,
      name: candidate.name ?? '',
      category: resolvedCategory.category || prev.category,
      subCategory: resolvedCategory.subCategory || '',
      contact: candidate.contact ?? '',
      manager: candidate.managerName ?? '',
      email: candidate.email ?? '',
      address: candidate.address ?? '',
      businessNumber: candidate.businessNumber ? formatBusinessNumber(candidate.businessNumber) : '',
      bankName: candidate.bankName ?? '',
      accountNumber: candidate.accountNumber ?? '',
      accountHolder: candidate.accountHolder ?? '',
      memo: candidate.memo ?? ''
    }));
  };

  const handleSelectExistingVendor = (candidate: HostVendorCandidate) => {
    setSelectedExistingHostIdx(candidate.vendorIdx);
    setIsFinderOpen(false);
    applyExistingVendorToForm(candidate);
  };

  // 모달이 열리거나 preSelectedVendor가 변경될 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      const firstCategory = topCategories[0];
      const defaultCategoryName = firstCategory?.name || '';
      const defaultSubCategoryName = firstCategory?.children?.[0]?.name || '';
      setHostSearchTerm('');
      setHostCandidates([]);
      setHostCandidatesError(null);
      setSelectedExistingHostIdx(null);
      setIsFinderOpen(false);
      setHostFilterCategoryIdx(null);

      if (preSelectedVendor) {
        const matchedParent =
          topCategories.find((item) => item.name === preSelectedVendor.category) ||
          topCategories.find((item) => item.children?.some((child) => child.name === preSelectedVendor.subCategory));

        const categoryName = matchedParent?.name || defaultCategoryName;
        const matchedSub = matchedParent?.children?.find((child) => child.name === preSelectedVendor.subCategory);
        const subCategoryName = matchedSub?.name || matchedParent?.children?.[0]?.name || defaultSubCategoryName;

        setFormData({
          name: preSelectedVendor.name,
          category: categoryName,
          subCategory: subCategoryName,
          contact: preSelectedVendor.contact,
          manager: preSelectedVendor.manager,
          email: preSelectedVendor.email,
          address: preSelectedVendor.address,
          businessNumber: preSelectedVendor.businessNumber,
          bankName: '',
          accountNumber: '',
          accountHolder: '',
          memo: `WedlogBiz 파트너 업체 (평점: ${preSelectedVendor.rating})`
        });
      } else {
        setFormData({ ...initialFormState, category: defaultCategoryName, subCategory: defaultSubCategoryName });
      }
    }
  }, [isOpen, preSelectedVendor, topCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isFinderOpen) return;

    if (!hasExistingSearchCondition) {
      setIsHostCandidatesLoading(false);
      setHostCandidates([]);
      setHostCandidatesError(null);
      return;
    }

    let isUnmounted = false;
    const timer = setTimeout(async () => {
      setIsHostCandidatesLoading(true);
      setHostCandidatesError(null);
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          if (!isUnmounted) {
            setHostCandidates([]);
            setHostCandidatesError('로그인 정보가 없습니다. 다시 로그인 후 시도해주세요.');
          }
          return;
        }
        const params = new URLSearchParams();
        params.append('page', '1');
        params.append('size', '20');
        params.append('status', 'ALL');
        params.append('vendorType', 'host');
        if (hostFilterCategoryIdx) {
          const isParentCategory = topCategories.some((category) => category.vendorCategoryIdx === hostFilterCategoryIdx);
          if (isParentCategory) {
            params.append('parentCategoryIdx', String(hostFilterCategoryIdx));
          } else {
            params.append('categoryIdx', String(hostFilterCategoryIdx));
          }
        }
        if (hostSearchTerm.trim()) {
          params.append('searchTerm', hostSearchTerm.trim());
        }

        const hostCandidatesResponse = await fetch(
          `https://api.wedmin.biz/vendor/existing?${params.toString()}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await hostCandidatesResponse.json().catch(() => null);
        const payload = result?.data && typeof result.data === 'object' ? result.data : result;
        const rawVendors: unknown[] = Array.isArray(payload?.vendors) ? payload.vendors : [];

        if (!hostCandidatesResponse.ok || result?.status === false) {
          if (!isUnmounted) {
            setHostCandidates([]);
            setHostCandidatesError(
              result?.message || payload?.message || `기존 업체 목록 조회 실패 (${hostCandidatesResponse.status})`
            );
          }
          return;
        }

        const mappedCandidates: HostVendorCandidate[] = rawVendors
          .map((rawVendor) => {
            const vendor = rawVendor as Record<string, any>;
            const category = vendor?.category as Record<string, any> | undefined;
            const parentCategoryName =
              typeof category?.parent?.name === 'string'
                ? String(category.parent.name)
                : typeof vendor?.parentCategoryName === 'string'
                  ? String(vendor.parentCategoryName)
                  : undefined;
            const subCategoryName =
              typeof category?.name === 'string'
                ? String(category.name)
                : typeof vendor?.subCategoryName === 'string'
                  ? String(vendor.subCategoryName)
                  : typeof vendor?.categoryName === 'string'
                    ? String(vendor.categoryName)
                    : undefined;
            const fallbackCategoryIdx = Number(vendor?.vendorCategoryIdx || category?.vendorCategoryIdx || 0);
            let resolvedParentName = parentCategoryName;
            let resolvedSubName = subCategoryName;
            if ((!resolvedParentName || !resolvedSubName) && fallbackCategoryIdx > 0) {
              for (const parentItem of topCategories) {
                if (parentItem.vendorCategoryIdx === fallbackCategoryIdx) {
                  resolvedParentName = parentItem.name;
                  resolvedSubName = resolvedSubName || '';
                  break;
                }
                const matchedChild = parentItem.children?.find((child) => child.vendorCategoryIdx === fallbackCategoryIdx);
                if (matchedChild) {
                  resolvedParentName = resolvedParentName || parentItem.name;
                  resolvedSubName = resolvedSubName || matchedChild.name;
                  break;
                }
              }
            }
            const categoryName = resolvedParentName && resolvedSubName
              ? `${resolvedParentName} > ${resolvedSubName}`
              : resolvedSubName || resolvedParentName || '미지정';
            return {
              vendorIdx: Number(vendor?.vendorIdx || 0),
              name: String(vendor?.name || ''),
              categoryName,
              parentCategoryName: resolvedParentName,
              subCategoryName: resolvedSubName,
              managerName: String(vendor?.managerName || '-'),
              contact: String(vendor?.contact || ''),
              email: vendor?.email ? String(vendor.email) : undefined,
              address: vendor?.address ? String(vendor.address) : undefined,
              businessNumber: vendor?.businessNumber ? String(vendor.businessNumber) : undefined,
              bankName: vendor?.bankName ? String(vendor.bankName) : undefined,
              accountNumber: vendor?.accountNumber ? String(vendor.accountNumber) : undefined,
              accountHolder: vendor?.accountHolder ? String(vendor.accountHolder) : undefined,
              memo: vendor?.memo ? String(vendor.memo) : undefined
            };
          })
          .filter((vendor) => vendor.vendorIdx > 0 && vendor.name);

        if (!isUnmounted) {
          setHostCandidates(mappedCandidates);
          setHostCandidatesError(null);
        }
      } catch (error) {
        console.error('Fetch host candidates error:', error);
        if (!isUnmounted) {
          setHostCandidates([]);
          const message = error instanceof Error ? error.message : '';
          const lowered = message.toLowerCase();
          if (lowered.includes('failed to fetch') || lowered.includes('network') || lowered.includes('cors')) {
            setHostCandidatesError('기존 업체 목록 조회 실패: 서버 연결 또는 CORS 설정을 확인해주세요.');
          } else {
            setHostCandidatesError(message ? `기존 업체 목록 조회 실패: ${message}` : '기존 업체 목록 조회 중 오류가 발생했습니다.');
          }
        }
      } finally {
        if (!isUnmounted) {
          setIsHostCandidatesLoading(false);
        }
      }
    }, 250);

    return () => {
      isUnmounted = true;
      clearTimeout(timer);
    };
  }, [isOpen, isFinderOpen, hostSearchTerm, hostFilterCategoryIdx, hasExistingSearchCondition, topCategories]);

  const handleSubmit = async () => {
    if (selectedExistingHostIdx) {
      const success = await onSubmit({ selectedExistingHostIdx });
      if (success) {
        alert('기존 업체 연결이 완료되었습니다.');
        onClearSelection();
        onClose();
      }
      return;
    }

    if (!formData.name || !formData.contact || !formData.manager) {
      alert('필수 정보를 입력해주세요.');
      return;
    }
    if (formData.notiPhone && !isValidKoreanMobile010(formData.notiPhone)) {
      alert('알림톡 수신번호는 010으로 시작하는 휴대폰 번호만 입력 가능합니다. (예: 010-1234-5678)');
      return;
    }

    if (!hasCategoryOptions) {
      alert('사용 가능한 공통 카테고리가 없습니다. 관리자에게 문의해주세요.');
      return;
    }

    let targetCategoryIdx = 0;
    const parentCat = categoryList.find(c => c.name === formData.category);
    
    if (parentCat) {
      if ((parentCat.children?.length || 0) > 0 && !formData.subCategory) {
        alert('세부 분류를 선택해주세요.');
        return;
      }
      const subCat = parentCat.children?.find(s => s.name === formData.subCategory);
      targetCategoryIdx = subCat ? subCat.vendorCategoryIdx : parentCat.vendorCategoryIdx;
    }

    if (targetCategoryIdx === 0) {
      alert("유효한 카테고리를 선택해주세요.");
      return;
    }

    const requestBody: VendorCreateRequest = {
      name: formData.name,
      managerName: formData.manager,
      contact: formData.contact,
      categoryIdx: targetCategoryIdx,
      ...(formData.notiPhone.trim() ? { notiPhone: formData.notiPhone.trim() } : {}),
      ...(formData.faxNumber.trim() ? { faxNumber: formData.faxNumber.trim() } : {}),
      ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
      ...(formData.businessNumber.trim() ? { businessNumber: formData.businessNumber.trim() } : {}),
      ...(formData.address.trim() ? { address: formData.address.trim() } : {}),
      ...(formData.memo.trim() ? { memo: formData.memo.trim() } : {}),
      ...(formData.bankName.trim() ? { bankName: formData.bankName.trim() } : {}),
      ...(formData.accountNumber.trim() ? { accountNumber: formData.accountNumber.trim() } : {}),
      ...(formData.accountHolder.trim() ? { accountHolder: formData.accountHolder.trim() } : {})
    };

    const success = await onSubmit({
      selectedExistingHostIdx: null,
      newVendorForm: requestBody
    });
    if (success) {
      alert("업체가 등록되었습니다.");
      onClearSelection();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold">신규 업체 등록</h2>
            {preSelectedVendor && (
              <p className="text-sm text-green-600 mt-1">
                <i className="ri-check-line mr-1"></i>WedlogBiz 파트너 업체 정보가 자동으로 입력되었습니다
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
        
        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); }}>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <h3 className="text-base font-semibold text-blue-900">이미 사용 중인 업체 찾기</h3>
                <p className="mt-1 text-xs text-blue-800">
                  먼저 찾아보고, 없으면 아래 입력란으로 바로 신규 등록하세요.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedExistingHostIdx && (
                  <button
                    type="button"
                    onClick={() => setSelectedExistingHostIdx(null)}
                    className="text-xs text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                  >
                    선택 해제
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsFinderOpen((prev) => !prev)}
                  className="px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 whitespace-nowrap transition-colors flex items-center gap-1.5"
                >
                  <i className={isFinderOpen ? 'ri-close-line' : 'ri-search-line'}></i>
                  {isFinderOpen ? '검색 닫기' : '업체 검색'}
                </button>
              </div>
            </div>

            {selectedExistingHost && (
              <div className="mt-3 flex items-center gap-2 bg-white rounded-md border border-blue-200 px-3 py-2">
                <i className="ri-building-line text-blue-600"></i>
                <span className="text-sm font-medium text-gray-900">{selectedExistingHost.name}</span>
                <span className="text-xs text-gray-500">(ID: {selectedExistingHost.vendorIdx})</span>
              </div>
            )}

            {isFinderOpen && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2 relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={hostSearchTerm}
                      onChange={(e) => setHostSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2"
                      placeholder="업체명, 담당자, 연락처 검색"
                    />
                  </div>
                  <div>
                    <select
                      value={hostFilterCategoryIdx === null ? '' : String(hostFilterCategoryIdx)}
                      onChange={(e) => {
                        setSelectedExistingHostIdx(null);
                        const rawValue = e.target.value;
                        if (!rawValue) {
                          setHostFilterCategoryIdx(null);
                          return;
                        }
                        setHostFilterCategoryIdx(Number(rawValue));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8"
                    >
                      <option value="">카테고리 전체</option>
                      {topCategories.map((category) => (
                        <optgroup key={category.vendorCategoryIdx} label={category.name}>
                          <option value={category.vendorCategoryIdx}>{category.name} (전체)</option>
                          {(category.children || []).map((subCategory) => (
                            <option key={subCategory.vendorCategoryIdx} value={subCategory.vendorCategoryIdx}>
                              {subCategory.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="mt-2 text-xs text-gray-600">
                  {selectedHostFilterLabel === '전체'
                    ? '검색어 또는 카테고리 선택 시 조회됩니다.'
                    : `선택 카테고리: ${selectedHostFilterLabel}`}
                </p>
                {hostCandidatesError && <p className="mt-2 text-xs text-red-600">{hostCandidatesError}</p>}

                <div className="mt-3 border border-gray-200 rounded-md bg-white max-h-44 overflow-y-auto">
                  {!hasExistingSearchCondition ? (
                    <p className="px-3 py-3 text-sm text-gray-500">검색어를 입력하거나 카테고리를 선택해주세요.</p>
                  ) : isHostCandidatesLoading ? (
                    <p className="px-3 py-3 text-sm text-gray-500">업체 목록을 불러오는 중입니다...</p>
                  ) : hostCandidates.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-gray-500">
                      검색 결과가 없습니다. 신규 업체일 가능성이 높습니다. 아래 입력란으로 등록을 진행하세요.
                    </p>
                  ) : (
                    hostCandidates.map((candidate) => {
                      const isSelected = candidate.vendorIdx === selectedExistingHostIdx;
                      return (
                        <button
                          key={candidate.vendorIdx}
                          type="button"
                          onClick={() => {
                            handleSelectExistingVendor(candidate);
                          }}
                          className={`w-full px-3 py-2 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className={`text-sm font-medium ${isSelected ? 'text-blue-800' : 'text-gray-800'}`}>
                                {candidate.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {candidate.categoryName} · 담당 {candidate.managerName} · 연락처 {maskMobile(candidate.contact)}
                              </p>
                            </div>
                            {isSelected && <i className="ri-check-line text-blue-600 mt-0.5"></i>}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {!isHostCandidatesLoading && hasExistingSearchCondition && hostCandidates.length === 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setIsFinderOpen(false)}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      신규 등록 계속
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <fieldset disabled={isExistingVendorSelected} className={`space-y-6 ${isExistingVendorSelected ? 'opacity-70' : ''}`}>
          {/* 기본 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">기본 정보</h3>
            {selectedExistingHostIdx && (
              <p className="mb-3 text-xs text-gray-500">
                찾은 업체 연결로 진행됩니다. 아래 입력값은 신규 업체를 등록할 때만 사용됩니다.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">업체명 *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="업체명을 입력하세요" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">담당자 *</label>
                <input type="text" value={formData.manager} onChange={(e) => setFormData({...formData, manager: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="담당자명을 입력하세요" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">대표 연락처 *</label>
                <input
                  type="tel"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="예: 02-1234-5678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">알림톡 수신번호</label>
                <input
                  type="tel"
                  value={formData.notiPhone}
                  onChange={(e) => setFormData({ ...formData, notiPhone: formatMobile010(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="010-0000-0000"
                  maxLength={13}
                />
                <p className="mt-1 text-xs text-gray-500">010 휴대폰 번호만 입력 가능</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">팩스번호</label>
                <input
                  type="tel"
                  value={formData.faxNumber}
                  onChange={(e) => setFormData({ ...formData, faxNumber: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="예: 02-1234-5678"
                />
              </div>
            </div>
          </div>

          {/* 분류 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">분류 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">업체 분류</label>
                <select 
                  value={formData.category}
                  onChange={(e) => {
                    setSelectedExistingHostIdx(null);
                    setFormData({
                      ...formData, 
                      category: e.target.value,
                      subCategory: categories[e.target.value]?.[0] || ''
                    });
                  }}
                  disabled={!hasCategoryOptions}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8"
                >
                  {!hasCategoryOptions && <option value="">카테고리 없음</option>}
                  {Object.keys(categories).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">세부 분류</label>
                <select 
                  value={formData.subCategory}
                  onChange={(e) => {
                    setSelectedExistingHostIdx(null);
                    setFormData({...formData, subCategory: e.target.value});
                  }}
                  disabled={!hasCategoryOptions || subCategoryOptions.length === 0}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8"
                >
                  {subCategoryOptions.length === 0 && <option value="">세부 분류 없음</option>}
                  {subCategoryOptions.map((sub) => (
                    <option key={sub.vendorCategoryIdx} value={sub.name}>{sub.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 사업 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">사업 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사업자번호</label>
                <input 
                  type="text" 
                  value={formData.businessNumber} 
                  onChange={(e) => setFormData({...formData, businessNumber: formatBusinessNumber(e.target.value)})} 
                  className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  placeholder="123-45-67890" 
                  maxLength={12}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="예: 신한은행"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">계좌번호</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="계좌번호를 입력하세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예금주</label>
                <input
                  type="text"
                  value={formData.accountHolder}
                  onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="예금주명을 입력하세요"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
              <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="전체 주소를 입력하세요" />
            </div>
          </div>

          {/* 메모 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <textarea value={formData.memo} onChange={(e) => setFormData({...formData, memo: e.target.value})} maxLength={500} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 resize-none" />
          </div>
          </fieldset>
          
          <div className="flex justify-end gap-3 mt-8">
            <button type="button" onClick={onClose} className="px-6 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer">취소</button>
            <button type="button" onClick={handleSubmit} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap cursor-pointer">
              {selectedExistingHostIdx ? '연결하기' : '등록'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
